import * as React from 'react'
import { useTranslation } from 'react-i18next'

import type { ShipmentContractFeedItem } from '#/entities/shipment'
import { ContractIcon } from '#/shared/ui/icons'
import {
  HistoryAppTag,
  HistoryRow,
  HistoryViewLink,
} from '#/shared/ui/history-row'
import { formatNoteTimestamp } from '#/shared/lib/format'

const CONTRACT_TYPE_LABEL_KEY: Record<string, string> = {
  shipping_order: 'orderDetails.history.contract.types.shipping_order',
  bill_of_lading: 'orderDetails.history.contract.types.bill_of_lading',
}

const CONTRACT_STATUS_LABEL_KEY: Record<string, string> = {
  draft: 'orderDetails.history.contract.status.draft',
  sent: 'orderDetails.history.contract.status.sent',
  viewed: 'orderDetails.history.contract.status.viewed',
  signed: 'orderDetails.history.contract.status.signed',
  expired: 'orderDetails.history.contract.status.expired',
  cancelled: 'orderDetails.history.contract.status.cancelled',
}

function useTypeLabel() {
  const { t } = useTranslation()
  return (type: string) => {
    const key = CONTRACT_TYPE_LABEL_KEY[type]
    return key ? t(key) : type
  }
}

function useStatusLabel() {
  const { t } = useTranslation()
  return (status: string) => {
    const key = CONTRACT_STATUS_LABEL_KEY[status]
    return key ? t(key) : status
  }
}

function useContractMeta() {
  const { t } = useTranslation()
  const statusLabel = useStatusLabel()
  return (item: ShipmentContractFeedItem): React.ReactNode[] => {
    const meta: React.ReactNode[] = []
    if (item.sentAt) meta.push(formatNoteTimestamp(item.sentAt))
    if (item.isApp) meta.push(<HistoryAppTag />)
    if (item.recipient) meta.push(item.recipient)
    if (item.status) meta.push(statusLabel(item.status))
    meta.push(
      item.signed
        ? t('orderDetails.history.contract.status.signed')
        : t('orderDetails.history.contract.status.notSigned'),
    )
    if (item.expiresAt) {
      meta.push(
        `${t('orderDetails.history.contract.expires')} ${formatNoteTimestamp(item.expiresAt)}`,
      )
    }
    return meta
  }
}

function ContractViewAction({ url }: { url: string }) {
  const { t } = useTranslation()
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="contents"
    >
      <HistoryViewLink>
        {t('orderDetails.history.actions.view')}
      </HistoryViewLink>
    </a>
  )
}

export function ContractHistoryRow({
  contract,
}: {
  contract: ShipmentContractFeedItem
}) {
  const typeLabel = useTypeLabel()
  const buildMeta = useContractMeta()
  return (
    <HistoryRow
      icon={ContractIcon}
      title={typeLabel(contract.contractType)}
      meta={buildMeta(contract)}
      action={
        contract.previewUrl ? (
          <ContractViewAction url={contract.previewUrl} />
        ) : undefined
      }
    />
  )
}
